import { useCallback, useEffect, useRef, useState } from 'react'
import { assistantChatStream } from '../../generic/Assistant_Api'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { clearAssistantConversation, restoreAssistantConversation, saveAssistantConversation } from '../../generic/utilities/assistant_conversation_utils'
import { localeCodeOf } from '../../generic/utilities/customLanguage'

// Jupiter's i18n uses 'eus' for Basque; the assistant expects standard codes
const toAssistantLanguage = (uiLanguage) => uiLanguage === 'eus' ? 'eu' : uiLanguage

const inertAbort = { abort: () => {} }

// Answers carry UI metadata (truncated, charts) that is not part of the
// contract; only role and content travel back as history. A chart travels as a
// short mark so the assistant knows it was shown, never with its data.
const toHistory = (messages) => messages.map(({ role, content, charts = [] }) => ({
  role,
  content: content + charts.map(chart => `\n[chart: ${chart.title}]`).join('')
}))

// Chart selector of the chat, remembered per browser
export const CHARTS_MODES = ['auto', 'never', 'always']
const CHARTS_MODE_KEY = 'mic.assistant.chartsMode'
const readChartsMode = () => {
  try {
    const stored = window.localStorage.getItem(CHARTS_MODE_KEY)
    return CHARTS_MODES.includes(stored) ? stored : 'auto'
  } catch (exception) {
    return 'auto'
  }
}

/**
 * Holds the assistant conversation state. The server is stateless, so the
 * full history lives here and travels with every turn. Answers arrive over
 * SSE: an empty assistant message is appended on send and filled in as the
 * tokens stream.
 *
 * - messages: [{ role: 'user' | 'assistant', content, charts? }]
 * - chartsMode / setChartsMode: the chart selector ('auto' | 'never' | 'always')
 * - sending: true from send until the stream finishes
 * - activity: name of the tool the assistant is executing right now, or ''
 * - error: '' | 'timeout' | 'unavailable' | 'interrupted' (translation key
 *   suffix for the UI; 'interrupted' means a reload cut a turn short)
 * - errorReference: the gateway's request_id for that failure, to correlate
 *   with its logs, or '' when the failure never reached the gateway
 * - cancelStreaming: aborts the generation in course (e.g. on unmount)
 * - retryLastTurn: replays the last question after a failure
 * - pendingAction: write the assistant proposes, waiting for confirmation
 *   ({ id, name, arguments }), or {} when there is none
 * - preparingAction: true while the assistant is preparing such a write
 * - confirmPendingAction / cancelPendingAction: resolve that confirmation
 *
 * The conversation is mirrored in sessionStorage so a reload does not lose it.
 */
const useAssistantChat = ({ onConnectionLost = () => {} } = {}) => {
  const [restored] = useState(restoreAssistantConversation)
  const [messages, setMessages] = useState(restored.messages)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(restored.interrupted ? 'interrupted' : '')
  const [errorReference, setErrorReference] = useState('')
  const [activity, setActivity] = useState('')
  const [preparingAction, setPreparingAction] = useState(false)
  const [pendingAction, setPendingAction] = useState({})
  const [chartsMode, setChartsModeState] = useState(readChartsMode)
  const abortControllerRef = useRef(inertAbort)
  const { t_, i18n } = useSmartTranslation()

  // Keep the conversation across reloads of the same browser session. Writes
  // are skipped while the answer streams — it would rewrite the whole
  // conversation on every token — and the in-flight turn is saved once, when
  // it starts, so a reload can tell it never finished.
  useEffect(() => {
    if (!sending) {
      saveAssistantConversation(messages)
    }
  }, [messages, sending])

  const setChartsMode = useCallback((mode) => {
    if (!CHARTS_MODES.includes(mode)) {
      return
    }
    setChartsModeState(mode)
    try {
      window.localStorage.setItem(CHARTS_MODE_KEY, mode)
    } catch (exception) {
      // Without storage the choice lasts until the page reloads
    }
  }, [])

  const cancelStreaming = useCallback(() => {
    abortControllerRef.current.abort()
  }, [])

  const runTurn = async (trimmed, history, confirmAction) => {
    // The turn is written whole: the question plus the empty bubble the stream
    // will fill in, flagged unfinished until a done event replaces it
    const inFlight = [
      ...history,
      { role: 'user', content: trimmed },
      { role: 'assistant', content: '', unfinished: true }
    ]
    setMessages(inFlight)
    saveAssistantConversation(inFlight)
    setSending(true)
    setError('')
    setErrorReference('')
    setPendingAction({})
    const controller = new AbortController()
    abortControllerRef.current = controller
    let streamedAnswer = ''
    let streamedCharts = []

    // Without meta the answer is not complete yet, so it stays marked unfinished
    const paintAnswer = (content, meta) => {
      const answer = meta ? { role: 'assistant', content, ...meta } : { role: 'assistant', content, unfinished: true }
      setMessages(previous => [...previous.slice(0, -1), answer])
    }
    const dropEmptyAnswer = () => {
      setMessages(previous => {
        const last = previous[previous.length - 1] || {}
        return last.role === 'assistant' && last.content === '' ? previous.slice(0, -1) : previous
      })
    }

    try {
      const result = await assistantChatStream({
        message: trimmed,
        history: toHistory(history),
        language: toAssistantLanguage(localeCodeOf(i18n.language)),
        charts: chartsMode,
        onChart: (chart) => {
          streamedCharts = [...streamedCharts, chart]
        },
        onToken: (fragment) => {
          streamedAnswer += fragment
          paintAnswer(streamedAnswer)
        },
        onTool: (tool) => {
          setActivity(tool.status === 'start' ? tool.name : '')
          setPreparingAction(tool.status === 'pending')
        },
        confirmAction,
        signal: controller.signal
      })
      // The done event's answer is the definitive text for the history (the
      // streamed tokens may include a preamble said before calling a tool)
      const finalAnswer = result.answer || streamedAnswer
      if (finalAnswer) {
        // truncated travels with the message: it describes that answer, and the
        // user must know if what they are reading is incomplete. Only attached
        // when it says something, to keep the turns clean.
        const meta = {}
        if (result.truncated) {
          meta.truncated = true
        }
        const charts = result.charts && result.charts.length ? result.charts : streamedCharts
        if (charts.length) {
          meta.charts = charts
        }
        paintAnswer(finalAnswer, meta)
      } else {
        dropEmptyAnswer()
        setError('unavailable')
      }
      // A write the assistant proposes is never executed until confirmed here
      if (result.pending_action) {
        setPendingAction(result.pending_action)
      }
    } catch (exception) {
      if (exception.sessionExpired) {
        // The app is already heading to the re-login flow; the streamed text
        // must not be shown, it cannot explain what actually happened
        setMessages(previous => previous.slice(0, -1))
      } else if (streamedAnswer === '') {
        dropEmptyAnswer()
      }
      if (!exception.cancelled && !exception.sessionExpired) {
        setError(exception.timeout ? 'timeout' : 'unavailable')
        // kept so a failure reported from the plant can be found in the
        // gateway's logs instead of being just "it did not work"
        setErrorReference(exception.requestId || '')
        // A network-level failure may mean the gateway is gone: let the caller
        // re-check availability and hide the assistant if it no longer answers
        if (exception.status === 0) {
          onConnectionLost()
        }
      }
    } finally {
      setSending(false)
      setActivity('')
      setPreparingAction(false)
      abortControllerRef.current = inertAbort
    }
  }

  const sendMessage = async (text) => {
    const trimmed = text.trim()
    if (trimmed === '' || sending) {
      return
    }
    await runTurn(trimmed, messages)
  }

  // Replays the last question after a failure, so it does not have to be retyped
  const retryLastTurn = async () => {
    if (sending) {
      return
    }
    let lastUserIndex = -1
    messages.forEach((message, index) => {
      if (message.role === 'user') {
        lastUserIndex = index
      }
    })
    if (lastUserIndex === -1) {
      return
    }
    // Whatever followed the question (a partial or failed answer) is dropped:
    // runTurn rewrites the conversation from the history before it
    await runTurn(messages[lastUserIndex].content, messages.slice(0, lastUserIndex))
  }

  /**
   * Runs the write the assistant proposed. The action travels back untouched,
   * so the gateway executes exactly what the user saw and confirmed.
   */
  const confirmPendingAction = async () => {
    if (sending || !pendingAction.id) {
      return
    }
    const confirmation = t_('Yes, I confirm')
    await runTurn(confirmation, messages, pendingAction)
  }

  // Nothing is sent to the gateway: the action simply never happens
  const cancelPendingAction = () => {
    setPendingAction({})
  }

  const clearConversation = () => {
    setMessages([])
    setError('')
    setErrorReference('')
    setPendingAction({})
    clearAssistantConversation()
  }

  return {
    messages,
    sending,
    error,
    errorReference,
    activity,
    preparingAction,
    pendingAction,
    sendMessage,
    retryLastTurn,
    confirmPendingAction,
    cancelPendingAction,
    clearConversation,
    cancelStreaming,
    chartsMode,
    setChartsMode
  }
}

export default useAssistantChat
